import clsx from 'clsx';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import {
    POTENTIAL_BANDS,
    type PotentialBand,
    potentialBandIndex,
    potentialBandTranslationKey,
} from '../model/automation';
import { usePalette } from './PaletteProvider';

/**
 * Five dots, one per band, filled up to the band the value sits in. Reads at a glance without repeating
 * the band word in every row; the word lives in the tooltip and the accessible label.
 */
export const BandChip: FunctionComponent<{
    readonly band: PotentialBand | undefined;
    readonly className?: string;
    readonly label?: string;
    readonly testId?: string;
}> = ({ band, className, label: customLabel, testId }) => {
    const translate = useTranslate();
    const { bandColor } = usePalette();
    const filled = band === undefined ? 0 : potentialBandIndex(band) + 1;
    const label =
        customLabel ??
        `${translate('pages.ai-transformation.kpi.exposure')}: ${translate(potentialBandTranslationKey(band))}`;
    const fill = bandColor(band);

    return (
        <Tooltip delayDuration={120}>
            <Tooltip.Trigger asChild={true}>
                <span
                    aria-label={label}
                    className={clsx('inline-flex h-6 items-center gap-[3px] whitespace-nowrap px-xxs', className)}
                    data-band={band ?? 'no-data'}
                    data-testid={testId}
                    role="img"
                    tabIndex={customLabel === undefined ? undefined : 0}
                >
                    {POTENTIAL_BANDS.map((slot, index) => (
                        <span
                            key={slot}
                            aria-hidden={true}
                            className="block size-2 shrink-0 rounded-full"
                            style={{
                                backgroundColor: fill,
                                opacity: index < filled ? 1 : 0.22,
                            }}
                        />
                    ))}
                </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">{label}</Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};
